/**
 * AS CORES DA AGENDA — a paleta fixa e a cor livre.
 *
 * Este módulo mora em `src/` mas é usado NOS DOIS LADOS: a tela pinta as
 * células com ele e o servidor valida com ele o que chega no campo `cor`
 * (mesmo arranjo já usado por `src/skills-catalogo.ts`, importado pelo
 * `server/colaboradores-espelho.ts`). Ter uma regra só é o ponto: se a tela
 * aceitasse uma cor que o servidor recusa — ou o contrário — a marcação
 * sumiria ou apareceria sem cor, e ninguém entenderia por quê.
 *
 * COMO A COR É GUARDADA
 * ---------------------
 * A coluna `agenda.cor` é texto livre e guarda uma de duas coisas:
 *   • uma CHAVE da paleta fixa ("verde", "vermelho", "azul"…), que vira a
 *     classe CSS `ag-verde` — as cores do cronograma da SERCAMP;
 *   • uma COR LIVRE em hexadecimal ("#7c3aed"), escolhida pela pessoa na
 *     paleta do sistema operacional. Aí não existe classe: a célula é pintada
 *     por estilo em linha.
 *
 * POR QUE VALIDAR O HEXADECIMAL COM TANTO RIGOR
 * ---------------------------------------------
 * Porque esse valor vai parar DENTRO de um `style` no HTML. Um texto qualquer
 * gravado no campo `cor` viraria CSS no navegador de quem abrisse a agenda.
 * Por isso `corLivre` só devolve algo quando o valor é exatamente
 * `#rgb`/`#rrggbb`; qualquer outra coisa vira `null` e cai no branco.
 */

/** Uma cor da paleta fixa. `nome` é só o nome da cor — nunca um significado. */
export interface CorAgenda {
  key: string;
  nome: string;
}

/**
 * Paleta fixa (as mesmas cores do cronograma da SERCAMP).
 *
 * O `nome` descreve a COR, e isso é de propósito: cada uma já carregou um
 * significado fixo ("Treinamento", "Externo (rosa)") que não correspondia ao
 * uso real — quem escreve o que a marcação significa é quem digita o serviço,
 * na mesma tela. O nome sobrou só para o `title` e o leitor de tela.
 */
export const AGENDA_CORES: CorAgenda[] = [
  { key: "preagendado", nome: "Amarelo claro (tracejado)" },
  { key: "confirmado", nome: "Verde escuro" },
  { key: "pessego", nome: "Pêssego" },
  { key: "lavanda", nome: "Azul claro" },
  { key: "rosa", nome: "Rosa" },
  { key: "rosa-forte", nome: "Rosa forte" },
  { key: "verde", nome: "Verde" },
  { key: "marrom", nome: "Marrom" },
  { key: "laranja", nome: "Laranja" },
  { key: "vermelho", nome: "Vermelho" },
  { key: "ciano", nome: "Ciano" },
  { key: "azul", nome: "Azul" },
  { key: "amarelo", nome: "Amarelo" },
  { key: "branco", nome: "Branco" },
];

const CHAVES = new Set(AGENDA_CORES.map((c) => c.key));

/** A cor é uma das da paleta fixa? */
export function ehCorDaPaleta(cor: unknown): boolean {
  return typeof cor === "string" && CHAVES.has(cor.trim());
}

/**
 * Normaliza uma COR LIVRE. Devolve "#rrggbb" em minúsculas, ou null.
 *
 * Aceita a forma curta (`#abc`) porque é o que alguém digita à mão; o
 * `<input type="color">` sempre manda a longa. Fora desses dois formatos,
 * null — e é essa recusa que impede texto arbitrário de virar CSS na tela de
 * quem abre a agenda (ver o cabeçalho).
 */
export function corLivre(cor: unknown): string | null {
  if (typeof cor !== "string") return null;
  const v = cor.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(v)) return v;
  if (/^#[0-9a-f]{3}$/.test(v)) {
    // #abc → #aabbcc (o navegador entende os dois, o banco guarda um só).
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  }
  return null;
}

/**
 * O valor que pode ser gravado no banco, a partir do que a tela mandou.
 *
 * Cai em "branco" quando não reconhece nada — a marcação existe e aparece,
 * só sem cor. Perder o dia inteiro do colaborador por causa de um campo de
 * cor esquisito seria um estrago muito maior do que uma célula branca.
 */
export function corParaGravar(cor: unknown, padrao = "branco"): string {
  const v = String(cor ?? "").trim();
  if (ehCorDaPaleta(v)) return v;
  return corLivre(v) ?? padrao;
}

/**
 * Preto ou branco por cima da cor escolhida?
 *
 * Luminância relativa (a fórmula do WCAG, sem a correção de gama — o suficiente
 * para decidir entre duas opções). Sem isto, alguém escolhe um roxo escuro na
 * paleta livre e o nome do serviço fica preto sobre roxo: ilegível justamente
 * na informação que importa.
 */
export function textoSobre(hex: string): string {
  const v = corLivre(hex);
  if (!v) return "#20303a";
  const r = parseInt(v.slice(1, 3), 16);
  const g = parseInt(v.slice(3, 5), 16);
  const b = parseInt(v.slice(5, 7), 16);
  const luz = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luz > 0.6 ? "#20303a" : "#ffffff";
}

/**
 * Como pintar um elemento com esta cor.
 *
 * Devolve a classe da paleta OU o estilo em linha da cor livre — nunca os
 * dois. Quem renderiza (célula da grade, quadradinho do seletor) espalha o
 * resultado e não precisa saber de qual dos dois mundos a cor veio.
 */
export function pintar(cor: string | null | undefined): {
  className: string;
  style?: { background: string; color: string; borderColor?: string };
} {
  const v = String(cor ?? "").trim();
  if (ehCorDaPaleta(v)) return { className: `ag-${v}` };
  const livre = corLivre(v);
  if (livre) {
    return {
      // `ag-livre` não pinta nada: só marca no HTML que esta cor veio da
      // paleta do usuário, o que ajuda a entender a tela ao depurar.
      className: "ag-livre",
      style: { background: livre, color: textoSobre(livre) },
    };
  }
  // Cor desconhecida (registro antigo, valor estranho): branco, como sempre foi.
  return { className: "ag-branco" };
}

/** Cor sugerida quando a pessoa abre a paleta livre pela primeira vez. */
export const COR_LIVRE_PADRAO = "#7c3aed";

/**
 * COR AUTOMÁTICA PELO TEXTO — as três marcações do dia a dia.
 *
 * Quem monta a agenda digita FALTA, FOLGA ou SERCAMP e sempre pintava à mão
 * com a mesma cor. Agora a tela pinta sozinha, enquanto a pessoa digita:
 *   FALTA   → vermelho
 *   FOLGA   → azul turquesa ("ciano")
 *   SERCAMP → verde escuro ("confirmado", o verde da marca)
 * (As cores foram confirmadas por quem monta a agenda em 24/09/2026.)
 * Vale quando o texto COMEÇA pela palavra ("FOLGA", "Folga BH", "SERCAMP -
 * BASE"); maiúsculas e acentos não contam. "TREINAMENTO SERCAMP" não pinta:
 * ali SERCAMP é o local de outra coisa, não a marcação.
 */
const CORES_POR_TEXTO: Record<string, string> = {
  FALTA: "vermelho",
  FOLGA: "ciano",
  SERCAMP: "confirmado",
};

/** Qual das três palavras o texto marca — ou null quando não é nenhuma. */
export function palavraDaCor(texto: string): string | null {
  const t = texto.normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toUpperCase();
  const m = /^(FALTA|FOLGA|SERCAMP)(?![A-Z0-9])/.exec(t);
  return m ? m[1] : null;
}

/** A cor que o texto pede, ou null quando ele não é uma das três marcações. */
export function corPeloTexto(texto: string): string | null {
  const p = palavraDaCor(texto);
  return p ? CORES_POR_TEXTO[p] : null;
}
